/**
 * Vadeli > Gun ici LONG (yalniz yonetici, BILGI AMACLI) - kullanici 26.10: "nereden bilecegim ben
 * long acacagimi"; "vadeli paneline yapalim, sifirdan".
 *
 * Backend her 1 saatlik mum kapanisinda tarar (scanner/intraday_service.py); kural tutunca
 * bildirim gider. Kurallar research/indicator_search_intraday.py sinavini gecenlerdir (Mart-Haziran
 * secim, Temmuz-Ekim sinav). Giris = sinyal mumunun kapanisi, cikis = 4 saat sonra. Short yok:
 * sinavi gecen short kurali bulunamadi. Sinyalix emir gondermez.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { IntradayReport, IntradayRule, IntradaySignal } from "../api/types";
import { dateTime, hourMinute, price } from "../lib/format";
import { IconRefresh, Switch } from "./Ui";

function futuresUrl(symbol: string): string {
  return `https://www.binance.com/tr/futures/${encodeURIComponent(symbol)}`;
}

function num(v: number | null | undefined, digits = 2): string {
  return v == null || !Number.isFinite(v) ? "—" : v.toFixed(digits).replace(".", ",");
}

function signed(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}%${Math.abs(v).toFixed(digits).replace(".", ",")}`;
}

function tone(v: number | null | undefined): string {
  return v == null || v === 0 ? "" : v > 0 ? "tone-up" : "tone-down";
}

function remaining(exitDue: number, now: number): string {
  const ms = exitDue - now;
  if (ms <= 0) return "kapatma zamanı geldi";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h} sa ${m} dk kaldı` : `${m} dk kaldı`;
}

function RuleLine({ r }: { r: IntradayRule }) {
  return (
    <span className="in-rule">
      <strong>{r.name}</strong>
      <span>
        Geçmiş (Mar–Eki, {r.trades} işlem): %{num(r.win_pct, 0)} kazanan, işlem başına{" "}
        {signed(r.avg_pct)}
        {r.note ? ` · ${r.note}` : ""}
      </span>
    </span>
  );
}

function ActiveCard({ s, now }: { s: IntradaySignal; now: number }) {
  const due = s.exit_due <= now;
  return (
    <li className="in-card" data-due={due || undefined}>
      <div className="in-card-head">
        <strong className="in-coin">
          {s.base_asset}{" "}
          <span className="pill" data-tone="up">
            LONG
          </span>
        </strong>
        <span className={`in-live ${tone(s.live_pct)}`}>{signed(s.live_pct)}</span>
      </div>
      <div className="in-times">
        <span>
          Giriş <strong>{price(s.entry)}</strong> · {hourMinute(s.entry_time)}
        </span>
        <span className={due ? "in-due" : ""}>
          Kapat <strong>{hourMinute(s.exit_due)}</strong> · {remaining(s.exit_due, now)}
        </span>
      </div>
      {s.rules.map((r) => (
        <RuleLine key={r.key} r={r} />
      ))}
      <a className="btn" href={futuresUrl(s.symbol)} target="_blank" rel="noopener">
        Binance Vadeli'de aç
      </a>
    </li>
  );
}

export function IntradayPanel({ onError }: { onError: (message: string) => void }) {
  const [report, setReport] = useState<IntradayReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await api.admin.intraday());
    } catch (error) {
      onErrorRef.current(error instanceof ApiError ? error.message : "Gün içi fırsatlar alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = window.setInterval(() => void load(), 60_000);
    const tick = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      window.clearInterval(refresh);
      window.clearInterval(tick);
    };
  }, [load]);

  const toggleNotify = useCallback(async (next: boolean) => {
    try {
      await api.admin.intradayNotify(next);
      setReport((r) => (r ? { ...r, notify_enabled: next } : r));
    } catch (error) {
      onErrorRef.current(error instanceof ApiError ? error.message : "Ayar kaydedilemedi");
    }
  }, []);

  const scan = report?.last_scan;
  const active = report?.active ?? [];
  const recent = report?.recent ?? [];

  return (
    <>
      {report ? <p className="in-how">{report.how_tr}</p> : null}

      <div className="title-row">
        <p className="note" style={{ margin: 0 }}>
          {report?.scanning
            ? "Taranıyor…"
            : scan
              ? `Son tarama ${hourMinute(scan.bar_close_time)} · ${scan.symbols_scanned} coin`
              : "Henüz tarama yok"}
          {report ? ` · sonraki ${hourMinute(report.next_scan_at)}` : ""}
        </p>
        <button
          type="button"
          className="icon-btn"
          onClick={() => void load()}
          aria-label="Yenile"
          disabled={loading}
        >
          <IconRefresh spinning={loading} />
        </button>
      </div>

      <h3 className="in-h">Açık fırsatlar</h3>
      {report && active.length === 0 ? (
        <p className="in-empty">
          Şu an fırsat yok. Kural tuttuğunda burada belirir ve bildirim gelir. Son haftada
          günde ortalama ~14 fırsat geldi; çoğu, piyasa genel düşerken aynı saatte topluca.
        </p>
      ) : null}
      <ul className="in-cards">
        {active.map((s) => (
          <ActiveCard key={s.id} s={s} now={now} />
        ))}
      </ul>

      {report ? (
        <div className="in-notify">
          <span>Fırsat bildirimi</span>
          <Switch
            checked={report.notify_enabled}
            onChange={(v) => void toggleNotify(v)}
            label="Fırsat bildirimi"
          />
        </div>
      ) : null}

      <h3 className="in-h">Sonuçlar</h3>
      {report ? (
        <p className="in-summary">
          {report.closed_total
            ? `Canlı: ${report.closed_total} işlem · %${num(
                (report.closed_wins / report.closed_total) * 100,
                0,
              )} kazanan · işlem başına ortalama ${signed(report.closed_avg_net_pct)} (maliyet sonrası)`
            : "Henüz sonuçlanan işlem yok; her fırsat 4 saat sonra burada ölçülür."}
        </p>
      ) : null}
      {recent.length ? (
        <ul className="in-results">
          {recent.map((s) => (
            <li key={s.id}>
              <strong>{s.base_asset}</strong>
              <span>
                {dateTime(s.entry_time)} → {hourMinute(s.exit_due)}
              </span>
              <span className={`in-res ${tone(s.net_pct)}`}>{signed(s.net_pct)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {report ? (
        <details className="in-rules">
          <summary>Kurallar ve geçmiş test</summary>
          <p className="note">
            Hepsi 1 saatlik grafikte: koşul oluştuğu saatin kapanışında long, {report.hold_hours}{" "}
            saat sonra kapat. Mart–Haziran 2026'da seçildi, hiç bakılmamış Temmuz–Ekim'de sınandı;
            komisyon + kayma %{num(report.cost_pct)} dahil, kaldıraçsız. Short kuralı yok: sınavı
            geçen bulunamadı.
          </p>
          <ul>
            {report.rules.map((r) => (
              <li key={r.key}>
                <RuleLine r={r} />
                <span className="note">
                  Test (Tem–Eki): %{num(r.test_win_pct, 0)} kazanan, {signed(r.test_avg_pct)};
                  4 ayın {r.test_months_up}'ünde artı
                </span>
              </li>
            ))}
          </ul>
          <p className="note">Veri: {report.source_label}.</p>
        </details>
      ) : null}

      {report ? <p className="note">{report.disclaimer_tr}</p> : null}
    </>
  );
}
